import { createPortal } from 'react-dom';

import { SelectOption } from '~/shared/utils/types';
import Button from '~/shared/components/ButtonsV2';
import FilterIcon from '~/shared/assets/icons/filter.svg';
import CancelIcon from '~/shared/assets/icons/cancel.svg';
import Typography from '~/shared/components/Typography';
import { usePanelBehavior } from '~/shared/utils/usePanelBehavior';

import { useFilterState, useFilterUrlParams } from './hooks';
import FilterPanelHeader from './FilterPanelHeader';
import FilterPanelContent from './FilterPanelContent';
import FilterActions from './FilterActions';

export type GroupReportselectionOption = {
  label: string;
  options: SelectOption[];
};

interface ReportsFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters?: () => void;
  // Applied filters passed from parent
  appliedClusterNameFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedAutoscaleState: string[];
  activeFilterCount: number;
  setSidePanelOpen: (open: boolean) => void;
  isSidePanelOpen: boolean;
}

export default function ReportsFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  appliedClusterNameFilters,
  appliedInstanceFilters,
  appliedVolumeIdFilters,
  appliedVolumeTagFilters,
  appliedAutoscaleState,
  activeFilterCount,
  setSidePanelOpen,
  isSidePanelOpen,
}: ReportsFiltersSidePanelProps) {
  const { panelRef } = usePanelBehavior(open, onOpenChange);
  const { applyFilters, resetFilters } = useFilterUrlParams();

  const {
    pendingAutoscaleState,
    setPendingAutoscaleState,
    pendingClusterNameFilters,
    setPendingClusterNameFilters,
    pendingVolumeIdFilters,
    setPendingVolumeIdFilters,
    pendingVolumeTagFilters,
    setPendingVolumeTagFilters,
    pendingInstanceFilters,
    setPendingInstanceFilters,
    resetPendingFilters,
  } = useFilterState({
    open,
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAutoscaleState,
  });

  const handleResetFilters = () => {
    if (onResetFilters) {
      onResetFilters();
    } else {
      resetPendingFilters();
      resetFilters();
    }
  };

  const handleApplyFilters = () => {
    applyFilters(
      pendingAutoscaleState,
      pendingClusterNameFilters,
      pendingVolumeIdFilters,
      pendingVolumeTagFilters,
      pendingInstanceFilters
    );
  };

  const panel = (
    <div
      ref={panelRef}
      className={`fixed -mt[0.125px] right-0 z-[2000] w-[587px] bg-white border-l border-primary-70 transform transition-transform duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{
        top: 0,
        bottom: 0,
        boxShadow: '-15px 86px 57.4px -3px rgba(0, 0, 0, 0.09)',
      }}
    >
      <div className="flex flex-col h-full">
        <FilterPanelHeader />

        <FilterPanelContent
          panelOpen={open}
          pendingAutoscaleState={pendingAutoscaleState}
          setPendingAutoscaleState={setPendingAutoscaleState}
          pendingClusterNameFilters={pendingClusterNameFilters}
          setPendingClusterNameFilters={setPendingClusterNameFilters}
          pendingInstanceFilters={pendingInstanceFilters}
          setPendingInstanceFilters={setPendingInstanceFilters}
          pendingVolumeIdFilters={pendingVolumeIdFilters}
          setPendingVolumeIdFilters={setPendingVolumeIdFilters}
          pendingVolumeTagFilters={pendingVolumeTagFilters}
          setPendingVolumeTagFilters={setPendingVolumeTagFilters}
        />

        <FilterActions
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
          onClose={() => onOpenChange(false)}
          hasActiveFilters={
            pendingAutoscaleState.length > 0 ||
            pendingClusterNameFilters.length > 0 ||
            pendingVolumeIdFilters.length > 0 ||
            pendingVolumeTagFilters.length > 0 ||
            pendingInstanceFilters.length > 0
          }
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="flex gap-2 items-cente justify-end  w-full max-w-[713px] bg-gree">
        <Button
          size="md-responsive"
          variant="third"
          className="flex gap-1! items-center relative transition-all duration-800 w-auto group"
          leftIcon={
            <FilterIcon
              className={`size-5 aspect-square group-hover:stroke-primary-400  group-hover:stroke-2 ${
                activeFilterCount > 0 ? 'stroke-primary-400 stroke-2 ' : 'stroke-primary-900'
              }`}
            />
          }
          rightIcon={
            activeFilterCount > 0 && (
              <div
                className="px-[6px] h-[22px] bg-gray-200 hover:bg-gray-300 rounded transition-colors duration-150 flex gap-0.5 items-center "
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetFilters();
                }}
                title="Clear all filters"
              >
                <span
                  className="grid place-items-center w5 h6 rounded   text-primary-900 text-xs font-700 font-semibold   text-center"
                  data-testid="active-filter-count"
                >
                  {activeFilterCount}
                </span>
                <CancelIcon className="size-4 stroke-gray-700" />
              </div>
            )
          }
          onClick={() => {
            setSidePanelOpen(!isSidePanelOpen);
          }}
        >
          <Typography variant="p3" color="text-primary-900" className="whitespace-nowrap">
            Filters
          </Typography>
        </Button>
      </div>
      {typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
}
